<template>
  <div>
    <div
      v-if="!isShowShareRecipientList"
      class="relative"
    >
      <section class="share__presentation-online-link">
        <h3 class="flex items-center my-2" v-if="!hideTitle">
          <aha-icon-container
            class="my-2 mr-2 font-bold truncate cursor-pointer"
            size="md"
            icon-class="bg-secondary-40 text-base-0"
          >
            <aha-icon
              name="duplicated"
              width="14"
              height="14"
              viewBox="0 0 18 18"
            />
          </aha-icon-container>
          {{ $i18nPresenter.t('Share a copy') }}
        </h3>
        <p class="share__section-description" v-if="!hideSentToTeam">
          {{ $i18nPresenter.t(`Anyone with this link can view this presentation and copy it `
            + `(without audience responses) to their own account.`) }}
        </p>

        <aha-clipboard
          class="text-xs"
          :clipboard="() => {
            return getShareLink()
          }"
          :source="'share-a-copy'"
          @copy="copyLink('share-slide')"
          v-if="!hideSentToTeam"
        />

        <div class="flex share_section-v2 mt-2" v-if="hideSentToTeam">
          <aha-button :disabled="!canViewerShare" class="button-copy pl-4 pr-4" @click="copyURL()">
            <i class="fas fa-link mr-2"></i>
            {{ $i18nPresenter.t(`Copy link`) }}
          </aha-button>

          <span
            class="actions-kebab-menu"
            @click="clickKebabMenu"
          >
            <aha-icon
              width="16"
              height="16"
              viewBox="0 0 16 16"
              name="kebab-menu-share"
            />
          </span>
        </div>

        <div :class="{'hidden': !isHideShareLinkCheckbox}" class="mt-6">
          <CheckboxCopySlideNote
            :from="from"
            :presentation="presentation"
            class="mt-3"
          />

          <CheckboxDisableConversationPresentationShare
            :from="from"
            :presentation="presentation"
            class="mt-3"
            v-if="!isWhiteLabel && currentUser.identifyProvider !== 'skoletube' && !currentUser.isGuest"
          ></CheckboxDisableConversationPresentationShare>
        </div>
      </section>

      <div v-if="sendACopyVisible && !hideSentToTeam" class="pt-3 border-t border-base-30">
        <section class="pb-2">
          <p class="mb-2 text-xs">
            {{ $i18nPresenter.t('Send a copy of this presentation (with audience responses) to your team members.') }}
          </p>
          <a
            @click="handleShowSendACopyDialog()"
            class="font-bold text-link--primary btn-send-to"
          >
            {{ $i18nPresenter.t('Send to...') }}
          </a>
        </section>
      </div>
    </div>
    <div v-if="isShowShareRecipientList">
      <div
        class="sticky top-0 z-10 flex items-center justify-center shadow-top bg-base-0 border-base-30 h-15 text-base-90"
      >
        <span
          @click="handleBackButtonClick()"
          class="absolute cursor-pointer left-4"
        >
          <aha-icon name="arrow-left" />
        </span>
        <p class="text-sm font-bold text-secondary-100 send-copy-modal-title">
          {{ $i18nPresenter.t('Send a copy to your team members') }}
        </p>
      </div>
      <recipients-list
        class="px-4 pt-4 pb-16"
        :presentation="$store.state.presentation"
        v-if="isShowShareRecipientList"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import Config from '@/config'
import Utils from '@/util'
import constants from '@/constant'
// components
import RecipientsList from '@/components-v2/modals/RecipientsList.vue'
import CheckboxCopySlideNote from '@/components-v2/presentation-editor/collaboration/CheckboxCopySlideNote.vue'
import CheckboxDisableConversationPresentationShare from '@/components-v2/presentation-editor/collaboration/CheckboxDisableConversationPresentationShare.vue'
import eventCategories from '@/constant/eventCategories'

export default {
  components: {
    RecipientsList,
    CheckboxCopySlideNote,
    CheckboxDisableConversationPresentationShare,
  },
  props: {
    presentation: {
      type: Object,
      required: false,
      default() {
        return null
      },
    },
    hasPadding: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    hideSentToTeam: {
      type: Boolean,
      default: false,
    },
    from: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showTeamSetup: false,
      openChangeAccessCode: false,
      submitting: false,
      newAccessCode: '',
      textError: '',
      domain: '',
      presenterLink: '',
      isShowShareRecipientList: false,
      isHideShareLinkCheckbox: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canViewerShare: (state) => state.canViewerShare,
    }),
    sendACopyVisible() {
      if (this.currentUser.accountRole === 'viewer' && this.$store.state.workingMode === constants.workspace.TEAM) return false
      return this.currentUser.identifyProvider !== 'skoletube' && this.currentUser.accountId
    },
    isWhiteLabel() {
      return Utils.checkWhiteLabelDomain()
    },
  },
  mounted() {
    this.domain = Config.domain
    this.presenterLink = window.location.origin
    this.newAccessCode = (this.presentation && this.presentation.accessCode)
    || this.$store.state.presentation.accessCode

    if ((this.presentation && this.presentation.whiteLabelLink)
    || this.$store.state.presentation.whiteLabelLink) {
      if (this.presentation && this.presentation.whiteLabelLink) {
        this.domain = this.presentation.whiteLabelLink
      } else {
        this.domain = this.$store.state.presentation.whiteLabelLink
      }
    }
  },
  methods: {
    ...mapActions(['patchPresentationAction']),
    clickKebabMenu() {
      this.isHideShareLinkCheckbox = !this.isHideShareLinkCheckbox
      if (this.isHideShareLinkCheckbox) {
        Utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Share menu > Share slides > More feature',
          null,
          null,
          {
            presentationId: this.presentation.id,
            userId: this.currentUser.id,
            isPresentationOwner: this.presentation.userId === this.currentUser.id,
          },
        )
      }
    },
    getShareLink() {
      const { slugifiedName: slug, shareCode } = this.presentation
      if (slug && shareCode && shareCode.indexOf('-') > -1) {
        return `${this.presenterLink}/share/${slug}-${shareCode}`
      }
      return `${this.presenterLink}/share/${shareCode}`
    },
    handleBackButtonClick() {
      this.isShowShareRecipientList = false
      this.$emit('handle-hide-main-header')
    },
    copyLink() {
      Utils.showToastSuccess(this.$i18nPresenter.t('Link copied!'))
    },

    handleShowSendACopyDialog() {
      const route = this.$route.name
      if (route === 'PresentationDetail') {
        this.isShowShareRecipientList = true
        this.$emit('handle-hide-main-header')
      } else if (route === 'Presentations' || route === 'FolderDetail') {
        this.$emit('handleShowSendACopyDialog')
      }
    },
    async copyURL() {
      try {
        const url = this.getShareLink()
        await navigator.clipboard.writeText(url)
        this.copyLink()
      } catch (error) {
        Sentry.captureException(error)
      }
      this.trackClickShare()
    },
    // separate to easier to override if necessary
    trackClickShare() {
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Share menu > Share slides > Copy link',
        null,
        null,
        {
          presentationId: this.presentation.id,
          userId: this.currentUser.id,
          isPresentationOwner: this.presentation.userId === this.currentUser.id,
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>

.share__section-title {
  @apply mb-3 text-sm font-bold text-secondary-100;
}

.share__section-description {
  @apply mb-3 text-xs;
}
.share__presentation-online-link {
  @apply mb-3;
}
.button-copy {
  position: relative;
  top: -2px;
}

.share_section-v2 {
  input {
    padding: 0.4rem;
    display: flex;
    cursor: pointer;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    justify-content: space-between;
    width: 100%;
    border-radius: 0.25rem;
    border-width: 1px;
    --tw-border-opacity: 1;
    border-color: rgba(211,215,225,var(--tw-border-opacity));
    font-size: .875rem;
    line-height: 1.25rem;
    margin-right: 10px;
    background-color: #eff1f7;
  }
}

.actions-kebab-menu {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 12px;
  width: 32px;

  &:hover {
    background: #eff1f7;
    border-radius: 50px;
    cursor: pointer;
  }
}
</style>
